import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Statistic, theme } from 'antd';
import { 
  UserOutlined, 
  SettingOutlined, 
  CarOutlined, 
  ScheduleOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  TeamOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSystem } from '../contexts/SystemContext';


const { Title, Text } = Typography;
const { useToken } = theme;

interface DashboardStats {
  todayOrders: number;
  todayPieces: number;
  todayFreight: number;
  totalFreight: number;
}

const Dashboard: React.FC = () => {
  const { token } = useToken();
  const { systemSettings } = useSystem();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    todayOrders: 0,
    todayPieces: 0,
    todayFreight: 0,
    totalFreight: 0
  });
  const [loading, setLoading] = useState(true);

  // 获取统计数据
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/logistics-orders/dashboard/stats');
        if (response.ok) {
          const result = await response.json();
          if (result.code === 0) {
            setStats(result.data);
          }
        }
      } catch (error) {
        console.error('获取统计数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // 导航到指定页面
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // 导航分组数据
  const navigationSections = [
    {
      title: '物流收发集运',
      icon: <CarOutlined style={{ fontSize: '24px' }} />,
      description: '物流订单管理、新增收货',
      color: token.colorSuccess,
      items: [
        { 
          label: '创建物流单', 
          path: '/logistics/new-order', 
          icon: <FileTextOutlined style={{ fontSize: '32px', color: token.colorSuccess }} />,
          description: '快速创建新的物流订单'
        },
        { 
          label: '物流单列表', 
          path: '/logistics/orders', 
          icon: <DatabaseOutlined style={{ fontSize: '32px', color: token.colorSuccess }} />,
          description: '管理物流订单信息'
        }
      ]
    },
    {
      title: '基础数据',
      icon: <DatabaseOutlined style={{ fontSize: '24px' }} />,
      description: '收发货站点、收发货人、承运单位管理',
      color: token.colorPrimary,
      items: [
        { 
          label: '收发货站点', 
          path: '/shipping-stations', 
          icon: <EnvironmentOutlined style={{ fontSize: '32px', color: token.colorPrimary }} />,
          description: '管理收发货站点信息'
        },
        { 
          label: '收发货人管理', 
          path: '/consigners', 
          icon: <UserOutlined style={{ fontSize: '32px', color: token.colorPrimary }} />,
          description: '管理收发货人信息'
        },
        { 
          label: '承运单位管理', 
          path: '/carriers', 
          icon: <CarOutlined style={{ fontSize: '32px', color: token.colorPrimary }} />,
          description: '管理承运单位信息'
        }
      ]
    },
    {
      title: '员工管理',
      icon: <TeamOutlined style={{ fontSize: '24px' }} />,
      description: '员工信息管理、增删改查、导入导出',
      color: token.colorWarning,
      items: [
        { 
          label: '员工管理', 
          path: '/employees', 
          icon: <UserOutlined style={{ fontSize: '32px', color: token.colorWarning }} />,
          description: '管理系统员工信息'
        }
      ]
    },
    {
      title: '系统设置',
      icon: <SettingOutlined style={{ fontSize: '24px' }} />,
      description: '基本设置、设备管理、系统配置',
      color: token.colorInfo,
      items: [
        { 
          label: '基本设置', 
          path: '/system-settings/basic-settings', 
          icon: <SettingOutlined style={{ fontSize: '32px', color: token.colorInfo }} />,
          description: '系统基本配置'
        },
        { 
          label: '设备管理', 
          path: '/devices', 
          icon: <SettingOutlined style={{ fontSize: '32px', color: token.colorInfo }} />,
          description: '管理系统设备'
        }
      ]
    }
  ];

    return (
    <div style={{ padding: '24px', background: token.colorBgContainer, minHeight: '100vh' }}>
      <style>
        {`
          .ant-statistic-title {
            color: white !important;
            font-size: 16px !important;
            font-weight: 500 !important;
          }
        `}
      </style>
      {/* 欢迎标题 */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '32px 24px',
        borderRadius: '16px',
        marginBottom: '32px',
        border: 'none'
      }}>
        <Title level={2} style={{ 
          color: 'white', 
          margin: 0,
          textAlign: 'center',
          fontSize: '32px',
          fontWeight: 'bold'
        }}>
          {systemSettings?.system_name || '兴运物流管理系统V1.0'}
        </Title>
        <Text style={{ 
          color: 'rgba(255,255,255,0.9)', 
          fontSize: '18px',
          textAlign: 'center',
          display: 'block',
          marginTop: '12px',
          fontWeight: '500'
        }}>
          智能化的物流管理平台
        </Text>
      </div>
        
        {/* 四个数据指标 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable
            style={{ 
              background: '#1890ff',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
            onClick={() => handleNavigation('/logistics/orders')}
          >
            <div style={{ color: 'white' }}>
              <Statistic 
                title="今日订单" 
                value={stats.todayOrders} 
                prefix={<ScheduleOutlined style={{ color: 'white', fontSize: '24px' }} />}
                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
                loading={loading}
              />
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '500' }}>
              今日新增的物流订单数量
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable
            style={{ 
              background: '#52c41a',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
            onClick={() => handleNavigation('/logistics/orders')}
          >
            <div style={{ color: 'white' }}>
              <Statistic 
                title="今日物件数" 
                value={stats.todayPieces} 
                prefix={<CarOutlined style={{ color: 'white', fontSize: '24px' }} />}
                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
                loading={loading}
              />
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '500' }}>
              今日处理的货物总件数
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable
            style={{ 
              background: '#fa8c16',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
            onClick={() => handleNavigation('/logistics/orders')}
          >
            <div style={{ color: 'white' }}>
              <Statistic 
                title="今日运费" 
                value={`¥${(Number(stats.todayFreight) || 0).toFixed(2)}`} 
                prefix={<FileTextOutlined style={{ color: 'white', fontSize: '24px' }} />}
                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
                loading={loading}
              />
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '500' }}>
              今日产生的运费总额
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card 
            hoverable
            style={{ 
              background: '#722ed1',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
            onClick={() => handleNavigation('/logistics/orders')}
          >
            <div style={{ color: 'white' }}>
              <Statistic 
                title="总运费" 
                value={`¥${(Number(stats.totalFreight) || 0).toFixed(2)}`} 
                prefix={<KeyOutlined style={{ color: 'white', fontSize: '24px' }} />}
                valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
                loading={loading}
              />
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: '500' }}>
              今日产生的运费总额
            </Text>
          </Card>
        </Col>
      </Row>

      {/* 功能导航 */}
      <div style={{ marginBottom: '32px' }}>
        {navigationSections.map((section, index) => (
          <div key={index} style={{ marginBottom: '32px' }}>
            {/* 分组标题 */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '20px',
              padding: '0 4px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                color: section.color,
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                {section.icon}
                <span>{section.title}</span>
              </div>
            </div>
            
            {/* 导航卡片 */}
            <Row gutter={[16, 16]}>
              {section.items.map((item, itemIndex) => (
                <Col xs={24} sm={12} md={8} lg={6} key={itemIndex}>
                  <Card
                    hoverable
                    style={{ 
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      border: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => handleNavigation(item.path)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      e.currentTarget.style.borderColor = section.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                      e.currentTarget.style.borderColor = '#f0f0f0';
                    }}
                  >
                    <div style={{ 
                      textAlign: 'center',
                      padding: '20px 16px'
                    }}>
                      <div style={{ marginBottom: '16px' }}>
                        {item.icon}
                      </div>
                      <Title level={5} style={{ 
                        color: token.colorText,
                        margin: '0 0 8px 0',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}>
                        {item.label}
                      </Title>
                      <Text style={{ 
                        color: token.colorTextSecondary,
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }}>
                        {item.description}
                      </Text>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ))}
      </div>


    </div>
  );
};

export default Dashboard;
